import { forwardRef, memo, useMemo } from 'react';
import type { DividerSlotsAndSlotProps } from '@mui/joy/Divider';
import BaseDivider from '@mui/joy/Divider';
import { slowCn } from '../../lib/utils';
import type { DividerProps } from './types';

const colorVariableMap = {
  // todo: check if this is breaking
  'border-brand-secondary': 'var(--border-brand-secondary)',
  'border-brand-solid': 'var(--border-brand-solid)',
  'border-primary': 'var(--border-primary)',
  'border-secondary': 'var(--border-secondary)',
  'border-tertiary': 'var(--border-tertiary)',
  'border-success': 'var(--border-success)',
  'border-warning': 'var(--border-warning)',
  'border-error': 'var(--border-error)',
  'border-warning-solid': 'var(--border-warning-solid)',
  'border-success-solid': 'var(--border-success-solid)',
  'border-error-solid': 'var(--border-error-solid)',
};

const Divider = forwardRef<HTMLHRElement, DividerProps>((props, ref) => {
  const { className, color = 'border-tertiary', style, ...rest } = props;

  const colorVariableInClassName = useMemo(() => {
    // Sort by length to match the longest color variable first , for exact match
    // e.g. border-primary-solid should match before border-primary
    const sortedKeys = Object.keys(colorVariableMap).sort((a, b) => b.length - a.length);
    return sortedKeys.find((colorVariable) =>
      className?.includes(colorVariable),
    ) as DividerProps['color'];
  }, [className]);

  const dividerSlotProps = useMemo<DividerSlotsAndSlotProps['slotProps']>(
    () => ({
      root: {
        className: slowCn(
          'h-[unset]',
          className,
          props.children ? 'text-secondary' : 'text-transparent',
        ),
        style: {
          '--Divider-lineColor': colorVariableMap[colorVariableInClassName ?? color],
          ...style,
        },
      },
    }),
    [className, color, colorVariableInClassName, props.children, style],
  );

  return <BaseDivider ref={ref} slotProps={dividerSlotProps} {...rest} />;
});

Divider.displayName = 'Divider';

export default memo(Divider);
